import api from '@/utils/api';

// Types
export interface WizardStatusResponse {
  wizardCompleted: boolean;
  wizardStep: number;
  data: {
    productOrService?: 'Product' | 'Service';
    industry?: string;
    description?: string;
    serviceDescription?: string;
    keyPeople?: Array<{
      name: string;
      familyName?: string;
      linkedinUrl?: string;
      role: string;
      pictureUrl?: string;
    }>;
    brandColors?: {
      primary?: string;
      secondary?: string;
      accent?: string;
    };
    logoUrl?: string;
    brandGuidelines?: string;
    competitors?: Array<{ id: number; name: string }>;
    linkedinUrl?: string;
    contentTypes?: Array<{ id: number; category: string }>;
    dailyPostCount?: number;
    salesTarget?: {
      name?: string;
      industry?: string;
      role?: string;
    };
    pitchStrategy?: string;
    calendlyLink?: string;
    country?: string;
    countryCode?: string;
    timezone?: string;
  };
}

export interface IndustryOption {
  id: number;
  name: string;
  linkedinCode?: string;
}

export interface CompanyOption {
  id: number;
  name: string;
  website?: string;
}

export interface SaveStepResponse {
  success: boolean;
  wizardStep: number;
}

class WizardService {
  async getStatus(): Promise<WizardStatusResponse> {
    const response = await api.get('/wizard/status');
    return response.data;
  }

  async saveStep1(data: {
    productOrService: 'Product' | 'Service';
    industry: string;
    industryId?: number;
    description?: string;
    serviceDescription?: string;
  }): Promise<SaveStepResponse> {
    const response = await api.post('/wizard/step-1', data);
    return response.data;
  }

  async saveStep2(data: {
    keyPeople?: Array<{
      name: string;
      familyName?: string;
      linkedinUrl?: string;
      role: string;
      pictureUrl?: string;
    }>;
    brandColors?: {
      primary?: string;
      secondary?: string;
      accent?: string;
    };
    logoUrl?: string;
    brandGuidelines?: string;
  }): Promise<SaveStepResponse> {
    const response = await api.post('/wizard/step-2', data);
    return response.data;
  }

  async saveStep3(data: {
    competitorIds: number[];
  }): Promise<SaveStepResponse> {
    const response = await api.post('/wizard/step-3', data);
    return response.data;
  }

  async saveStep4(data: {
    linkedinUrl?: string;
  }): Promise<SaveStepResponse> {
    const response = await api.post('/wizard/step-4', data);
    return response.data;
  }

  async saveStep5(data: {
    contentTypeIds: number[];
    dailyPostCount?: number;
  }): Promise<SaveStepResponse> {
    const response = await api.post('/wizard/step-5', data);
    return response.data;
  }

  async saveStep6(data: {
    name: string;
    industry: string;
    role: string;
    monthlyAppointmentsTarget?: number;
    monthlyLeadsTarget?: number;
    monthlyRevenueTarget?: number;
  }): Promise<SaveStepResponse> {
    const response = await api.post('/wizard/step-6', data);
    return response.data;
  }

  async saveStep7(data: {
    strategy: string;
  }): Promise<SaveStepResponse> {
    const response = await api.post('/wizard/step-7', data);
    return response.data;
  }

  async saveStep8(data: {
    calendlyLink: string;
  }): Promise<SaveStepResponse> {
    const response = await api.post('/wizard/step-8', data);
    return response.data;
  }

  async complete(): Promise<{ success: boolean }> {
    const response = await api.post('/wizard/complete');
    return response.data;
  }

  async searchIndustries(query: string): Promise<IndustryOption[]> {
    const response = await api.get(`/wizard/industries?search=${encodeURIComponent(query)}`);
    return response.data;
  }

  async searchCompanies(query: string): Promise<CompanyOption[]> {
    const response = await api.get(`/wizard/companies?search=${encodeURIComponent(query)}`);
    return response.data;
  }
}

export const wizardService = new WizardService();

